// Heat exchanger vertex schema
// Two-stream heat exchanger. Dual elevation.

import { z } from 'zod';
import { BaseVertexSchema } from '../base/vertex';
import { ValueWithUnitSchema, PressureDropCurveSchema } from '../base/common';
import { ThermalCalculationHeatExchangerSchema } from '../base/thermal';

// --- HX Configuration ---

const HxConfigurationTypeSchema = z.enum([
  'no_hx_configuration',
  'single_pass_counter_current_flow',
  'single_pass_parallel_flow',
  'shell_and_tube_1_shell',
  'shell_and_tube_n_shell',
  'cross_flow_hcr_max_mixed',
  'cross_flow_hcr_min_mixed',
  'specify_hx_configuration_factor',
  'specify_hx_ntu',
]);

export const HxConfigurationSchema = z.object({
  configuration_type: HxConfigurationTypeSchema,
  num_shell_passes: z.number().int().optional(),
  /** Number of Transfer Units — standard HX performance metric */
  ntu: z.number().optional(),
  configuration_factor: z.number().optional(),
});

export type HxConfiguration = z.infer<typeof HxConfigurationSchema>;

export const UaAvailableSchema = z.object({
  heat_transfer_coefficient: ValueWithUnitSchema.optional(),
  heat_transfer_area: ValueWithUnitSchema.optional(),
  fouling_factor: ValueWithUnitSchema.optional(),
});

export type UaAvailable = z.infer<typeof UaAvailableSchema>;

export const HeatExchangerVertexSchema = BaseVertexSchema.extend({
  type: z.literal('heat_exchanger'),
  // C++ emits dual elevations only when is_set() (per as_json audit § 4).
  inlet_elevation: ValueWithUnitSchema.optional(),
  outlet_elevation: ValueWithUnitSchema.optional(),
  pressure_drop_curve: PressureDropCurveSchema,
  linked_device_id: z.string().optional(),
  hx_configuration: HxConfigurationSchema,
  ua_available: UaAvailableSchema.optional(),
  thermal_calculation: ThermalCalculationHeatExchangerSchema,
  temperature_tolerance: ValueWithUnitSchema.optional(),
});

export type HeatExchangerVertex = z.infer<typeof HeatExchangerVertexSchema>;
